import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Badge, Popover, Select, Space, Typography } from 'antd';
import { ReturnsPopover } from 'components/Custom/ReturnsPopover';
import { FilterSelect } from 'components/Custom/selects';
import { ScopeTable } from 'components/Custom/table';
import { formatBadgeCount, formatDisplayInteger } from 'i18n/arabicDigits';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarSizes } from 'utils/render-avatar';
import { BaseCard } from './BaseCard';
import { TeamAllocationCardProps } from './CardProps';
import { DASHBOARD_CARD_TITLE_KEYS } from './dashboardCardKeys';

const { Text } = Typography;

const DROPDOWN_OPEN_GUARD_MS = 250;

export const TeamAllocationCard: React.FC<TeamAllocationCardProps> = ({
  dashboardViewType,
  cardId,
  accountUsers,
  filteredUserData,
  selectedUserNames,
  tempSelectedUserNames,
  teamAllocationFilterOpen,
  hoveredTeamAllocation,
  setHoveredTeamAllocation,
  setSelectedUserNames,
  setTempSelectedUserNames,
  setTeamAllocationFilterOpen,
  handlePersonClick,
  getReturnsByPersonAndRole,
  createSubmission,
  findUserByName,
  renderRole,
  renderLastLogin,
  renderAvatar: renderAvatarProp,
  t,
}) => {
  const { i18n } = useTranslation();
  const dropdownOpenedAtRef = React.useRef(0);

  const renderSortIcon = ({ sortOrder }: { sortOrder?: string }) => {
    if (sortOrder === 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
    return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
  };

  // Close Team Allocation dropdown on unmount so Ant Design Select (Overflow/ResizeObserver) can tear down and avoid state-update-after-unmount warning
  React.useEffect(() => {
    return () => setTeamAllocationFilterOpen(false);
  }, [setTeamAllocationFilterOpen]);

  // Only show in Manager View
  if (dashboardViewType !== 'manager') return null;

  const cardTitle = t(DASHBOARD_CARD_TITLE_KEYS[cardId]!);

  return (
    <BaseCard
      cardId={cardId}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            {cardTitle}
          </Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Text
              style={{
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '12px',
                textAlign: 'left',
                color: 'var(--color-text-dark, #042a2a)',
                minWidth: 35,
              }}
            >
              {t('main.filters')}
            </Text>
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                paddingRight: '8px',
              }}
            >
              <Popover
                placement="topRight"
                destroyOnHidden={true}
                arrow={false}
                trigger="hover"
                open={selectedUserNames.length > 0 && hoveredTeamAllocation}
                content={
                  selectedUserNames.length > 0 ? (
                    <div>
                      {selectedUserNames.map((userName) => (
                        <div key={userName} style={{ marginBottom: '4px' }}>
                          {userName}
                        </div>
                      ))}
                    </div>
                  ) : null
                }
                onOpenChange={(open: boolean) => setHoveredTeamAllocation(open)}
                title={''}
                overlayStyle={{ zIndex: 9999 }}
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              >
                <Badge
                  style={{ backgroundColor: '#41AAAF' }}
                  count={formatBadgeCount(
                    selectedUserNames.length > 0 ? selectedUserNames.length : 0,
                    i18n.language
                  )}
                  offset={[-3, 10]}
                  overflowCount={99}
                >
                  <FilterSelect
                    placeholder={t('main.users')}
                    aria-label={t('main.users')}
                    width="100%"
                    height="45px"
                    mode="multiple"
                    styles={{
                      popup: {
                        root: {
                          minWidth: '340px',
                          width: 'max-content',
                          maxWidth: '90vw',
                          fontSize: 13,
                          borderRadius: 20,
                        },
                      },
                    }}
                    popupRender={(items) => (
                      <Space
                        direction="vertical"
                        style={{ width: '100%', paddingBottom: '10px' }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        {items}
                        <Space
                          style={{
                            float: 'inline-end',
                            margin: '0 10px',
                            width: '100%',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <ScopeButton
                            width="75px"
                            height="25px"
                            fontSize="12px"
                            lineheight="12px"
                            onClick={() => {
                              setSelectedUserNames([]);
                              setTempSelectedUserNames([]);
                              setTeamAllocationFilterOpen(false);
                            }}
                            color="cyan"
                            type="default"
                          >
                            {t('common.clear')}
                          </ScopeButton>
                          <ScopeButton
                            width="75px"
                            height="25px"
                            fontSize="12px"
                            lineheight="12px"
                            onClick={() => {
                              setSelectedUserNames(tempSelectedUserNames);
                              setTeamAllocationFilterOpen(false);
                            }}
                            type="primary"
                          >
                            {t('common.apply')}
                          </ScopeButton>
                        </Space>
                      </Space>
                    )}
                    onOpenChange={(open) => {
                      if (open) {
                        dropdownOpenedAtRef.current = Date.now();
                        setTeamAllocationFilterOpen(true);
                        setTimeout(
                          () => setTempSelectedUserNames(selectedUserNames),
                          0
                        );
                      } else {
                        if (
                          Date.now() - dropdownOpenedAtRef.current <
                          DROPDOWN_OPEN_GUARD_MS
                        ) {
                          return;
                        }
                        setTeamAllocationFilterOpen(false);
                      }
                    }}
                    open={teamAllocationFilterOpen}
                    onChange={(value: string[]) =>
                      setTempSelectedUserNames(value)
                    }
                    value={tempSelectedUserNames}
                    showSearch
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    className={
                      selectedUserNames.length > 0 ? 'selected-filter' : ''
                    }
                  >
                    {accountUsers && accountUsers.length > 0
                      ? accountUsers
                          .map((user) => {
                            if (!user) return null;
                            const fullName =
                              `${user.firstName || ''} ${
                                user.lastName || ''
                              }`.trim() ||
                              user.email ||
                              t('common.unknown');
                            return (
                              <Select.Option
                                key={user.userUuid || fullName}
                                value={fullName}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div style={{ marginRight: '12px' }}>
                                    {renderAvatarProp(
                                      user.firstName,
                                      user.lastName,
                                      AvatarSizes.DEFAULT,
                                      user.userUuid,
                                      user.profilePicture
                                    )}
                                  </div>
                                  <div>{fullName}</div>
                                </div>
                              </Select.Option>
                            );
                          })
                          .filter(Boolean)
                      : filteredUserData
                          .filter(
                            (user) =>
                              user.preparerCount > 0 || user.reviewerCount > 0
                          )
                          .map((user) => (
                            <Select.Option key={user.name} value={user.name}>
                              {user.name}
                            </Select.Option>
                          ))}
                  </FilterSelect>
                </Badge>
              </Popover>
            </div>
          </div>
        </div>
      }
    >
      <ScopeTable
        tableDataSource={filteredUserData
          .filter((user) => user.preparerCount > 0 || user.reviewerCount > 0)
          .map((user, index) => {
            const matchedUser = findUserByName(user.name);
            return {
              ...user,
              key: index,
              accountType: matchedUser?.accountType,
              lastLogin: matchedUser?.lastLogin,
              accountActivated: matchedUser?.accountActivated,
            };
          })}
        headerFontSize={12}
        columns={[
          {
            title: t('dashboard.name-of-user'),
            dataIndex: 'name',
            key: 'name',
            sorter: (a: any, b: any) =>
              (a.name || '').localeCompare(b.name || ''),
            sortIcon: renderSortIcon,
            render: (text: string) => (
              <Text style={{ fontSize: '14px', color: 'var(--color-text-dark, #042a2a)' }}>{text}</Text>
            ),
          },
          {
            title: t('users.accountType'),
            dataIndex: 'accountType',
            key: 'accountType',
            sorter: (a: any, b: any) =>
              String(a.accountType || '').localeCompare(
                String(b.accountType || '')
              ),
            sortIcon: renderSortIcon,
            render: renderRole,
          },
          {
            title: t('users.last-login'),
            dataIndex: 'lastLogin',
            key: 'lastLogin',
            sorter: (a: any, b: any) => {
              const dateA = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
              const dateB = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
              return dateA - dateB;
            },
            sortIcon: renderSortIcon,
            render: renderLastLogin,
          },
          {
            title: t('main.preparer'),
            dataIndex: 'preparerCount',
            key: 'preparerCount',
            align: 'center' as const,
            sorter: (a: any, b: any) =>
              (a.preparerCount ?? 0) - (b.preparerCount ?? 0),
            sortIcon: renderSortIcon,
            render: (count: number, record: any) => {
              const returns = getReturnsByPersonAndRole(
                record.name,
                'preparer'
              );
              const cellContent = (
                <Text
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-text-dark, #042a2a)',
                    fontWeight: 600,
                    cursor: count > 0 ? 'pointer' : 'default',
                  }}
                  onClick={
                    count > 0
                      ? () => handlePersonClick(record.name, 'preparer')
                      : undefined
                  }
                >
                  {formatDisplayInteger(count, i18n.language)}
                </Text>
              );
              return count > 0 && returns.length > 0 ? (
                <ReturnsPopover
                  returns={returns}
                  onClick={() => handlePersonClick(record.name, 'preparer')}
                  onCreateSubmission={createSubmission}
                >
                  {cellContent}
                </ReturnsPopover>
              ) : (
                cellContent
              );
            },
          },
          {
            title: t('main.reviewer'),
            dataIndex: 'reviewerCount',
            key: 'reviewerCount',
            align: 'center' as const,
            sorter: (a: any, b: any) =>
              (a.reviewerCount ?? 0) - (b.reviewerCount ?? 0),
            sortIcon: renderSortIcon,
            render: (count: number, record: any) => {
              const returns = getReturnsByPersonAndRole(
                record.name,
                'reviewer'
              );
              const cellContent = (
                <Text
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-text-dark, #042a2a)',
                    fontWeight: 600,
                    cursor: count > 0 ? 'pointer' : 'default',
                  }}
                  onClick={
                    count > 0
                      ? () => handlePersonClick(record.name, 'reviewer')
                      : undefined
                  }
                >
                  {formatDisplayInteger(count, i18n.language)}
                </Text>
              );
              return count > 0 && returns.length > 0 ? (
                <ReturnsPopover
                  returns={returns}
                  onClick={() => handlePersonClick(record.name, 'reviewer')}
                  onCreateSubmission={createSubmission}
                >
                  {cellContent}
                </ReturnsPopover>
              ) : (
                cellContent
              );
            },
          },
        ]}
        pagination={false}
        rowsPerPage={
          filteredUserData.filter(
            (user) => user.preparerCount > 0 || user.reviewerCount > 0
          ).length
        }
        enableColumnsCustomization={false}
        rowHeight={40}
        fontSize={12}
        showSizeChanger={false}
        getNewPageSize={() => {}}
        transactionType=""
        reportId=""
      />
    </BaseCard>
  );
};
